import API from '@/services/api';
import type { GameProps } from './types';
import { useAppSelector } from '@/hooks';
import Loader from '@CS/components/Loader';
import { User, MCD } from '@/services/app';
import type { ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import { capitalize } from '@CS/utils/String';
import { getUserUsername } from '@/redux/user';
import { ResponseError } from '@CS/components/Popups';
import type { ResponseErrorInfo } from '@/services/api/types';
import { Username, Leaderboard, SurvivalMode } from '@/components/Popups';
import { Suspense, useEffect, useState, useMemo, useRef, lazy } from 'react';

export const Game = ({ price = 0 }: { price?: number }) => {
  const [responseError, setResponseError] = useState<ResponseErrorInfo>();
  const [visibleSurvivalMode, setVisibleSurvivalMode] = useState(false);
  const [visibleLeaderboard, setVisibleLeaderboard] = useState(false);
  const [highscore, setHighscore] = useState<number | null>(null);

  const onCloseSurvivalMode = () => setVisibleSurvivalMode(false);
  const onGameRestart = () => ~setRestart(true) && onGameStart();
  const [visibleUsername, setVisibleUsername] = useState(false);
  const closeLeaderboard = () => setVisibleLeaderboard(false);

  const showLeaderboard = () => setVisibleLeaderboard(true);
  const cancelResponse = () => setResponseError(undefined);
  const onCloseUsername = () => setVisibleUsername(false);

  const username = useAppSelector(getUserUsername);
  const [restart, setRestart] = useState(false);
  const index = +(useParams().index ?? 0);

  const participant = useRef('');
  const userScore = useRef(0);

  const Game = useMemo(
    () =>
      lazy(
        ((index: number) =>
          Object.entries(
            import.meta.glob([
              // Paths to game applications main files in the
              // order set by `API.gameSlugs` array. e.g.:
              // '/<SUBMODULE_NAME>/src/app/index.ts'
            ])
          ).find(([game]) =>
            game.includes(
              API.getCampaignGameSlug(index).split('-').map(capitalize).join('')
            )
          )?.[1] as () => Promise<{ default: ComponentType<GameProps> }>)(index)
      ),
    [index]
  );

  const onGameStart = (burnPoints?: boolean) =>
    void API.createParticipant(index)
      .then(({ id }) => {
        participant.current = id;

        burnPoints && void MCD.burnUserPoints(price).then(User.checkUserPoints);
      })
      .catch(async (error: Response) =>
        setResponseError(await API.getResponseErrorInfo(error))
      );

  const onGameOver = (score: number) => {
    username === null && setVisibleUsername(true);
    setHighscore(Math.max(score, highscore ?? 0));

    API.submitScore(participant.current, score).catch(async (error: Response) =>
      setResponseError(await API.getResponseErrorInfo(error))
    );

    userScore.current = score;
    setRestart(false);
  };

  useEffect(() => {
    API.getHighestScore(index)
      .then(score => setHighscore(score))
      .catch(async (error: Response) =>
        setResponseError(await API.getResponseErrorInfo(error))
      );
  }, [index]);

  return (
    <Suspense fallback={<Loader />}>
      {/* eslint-disable-next-line react-hooks/static-components */}
      <Game
        onLeaderboard={showLeaderboard}
        onGameRestart={onGameRestart}
        onGameStart={onGameStart}
        onGameOver={onGameOver}
        highscore={highscore}
        username={username}
        restart={restart}
      />

      {visibleSurvivalMode && (
        <SurvivalMode onClose={onCloseSurvivalMode} price={price} />
      )}

      {visibleLeaderboard && (
        <Leaderboard onClose={closeLeaderboard} game={index} />
      )}

      {visibleUsername && <Username onClose={onCloseUsername} />}

      {responseError && (
        <ResponseError info={responseError} onClose={cancelResponse} />
      )}
    </Suspense>
  );
};
